import { DateText } from "@portal/BaseComponents/Date/DateText";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { SkeletonInput } from "@portal/BaseComponents/Skeleton/AntSkeleton";
import { AmountText } from "@portal/BaseComponents/Table/Components/Amount/AmountText";
import { InvoicesColumns } from "@portal/BaseComponents/Table/Components/Invoices/InvoicesColumn";
import { InvoicesText } from "@portal/BaseComponents/Table/Components/Invoices/InvoicesText";
import { mapInvoicesColumnPredicate } from "@portal/BaseComponents/Table/Components/Invoices/predicate";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { getCreditSourcesLabel } from "@portal/Components/Portal/PotalTabs/CreditNotes/predicate";
import { CreditMemoDetails } from "@portal/types/entities/credit-memo";
import { DescriptionsProps } from "antd";
import { sum } from "lodash";
import { useMemo } from "react";
import { DetailsViewLayout } from "../../DetailsView";

interface CreditNotesDetailsCompProps {
  data?: CreditMemoDetails;
  loading: boolean;
}
function CreditDetailsComp(props: CreditNotesDetailsCompProps) {
  const { data, loading } = props;
  function getInvoices(): InvoicesColumns[] {
    if (!data) return [];
    return data.invoices.map(mapInvoicesColumnPredicate);
  }

  const invoices = useMemo(getInvoices, [data]);
  const Title =
    loading && !data ? (
      <SkeletonInput active />
    ) : (
      <TypographyText style={{ color: "var(--gray-9)" }}>
        {data?.note?.trim() ? data?.note : "-"}
      </TypographyText>
    );
  // const Desc = (
  //   <DetailsContent
  //     heading="Description"
  //     content={
  //       loading ? (
  //         <SkeletonInput active />
  //       ) : (
  //         <TypographyText>{data?.note ?? "-"}</TypographyText>
  //       )
  //     }
  //   />
  // );
  const Reference =
    loading && !data ? (
      <SkeletonInput active />
    ) : (
      <TypographyText strong>{data?.reference}</TypographyText>
    );
  const CreditDate =
    !loading && data ? (
      <DateText value={data.date} />
    ) : (
      <SkeletonInput active />
    );
  const CreditAmount =
    !loading && data ? (
      <AmountText amount={data.amount} currency={data.currency} format="FULL" />
    ) : (
      <SkeletonInput active />
    );
  const CreditType =
    !loading && data ? (
      <TypographyText strong>
        {getCreditSourcesLabel(data?.source_type)}
      </TypographyText>
    ) : (
      <SkeletonInput />
    );
  const AppliedAmount =
    !loading && data ? (
      <AmountText
        amount={data.applied_amount}
        currency={data.currency}
        format="FULL"
      />
    ) : (
      <SkeletonInput active />
    );
  const UnAppliedAmount =
    !loading && data ? (
      <AmountText
        amount={data.unapplied_amount}
        currency={data.currency}
        format="FULL"
      />
    ) : (
      <SkeletonInput active />
    );
  const AssociatedInvoice = data ? (
    <Flex vertical space="12">
      {invoices.length
        ? invoices.map((invoice) => {
            return (
              <Flex key={invoice.id}>
                <InvoicesText
                  overRideStyle={{ width: "auto" }}
                  invoice={invoice}
                  hide={{ amount: true }}
                  textStyle={{
                    title: {
                      fs: "14",
                      ellipsis: { tooltip: invoice.title },
                    },
                  }}
                />
                <AmountText
                  amount={sum(
                    data.invoice_credit_memos[invoice.id].map((f) => f.amount)
                  )}
                  currency={data.currency}
                  format="FULL"
                  textProps={{ fs: "14" }}
                />
              </Flex>
            );
          })
        : "-"}
    </Flex>
  ) : (
    <SkeletonInput />
  );

  function getItems(): DescriptionsProps["items"] {
    return [
      { key: "NOTES", label: "Notes", children: Title, span: 2 },
      { key: "REF", label: "Reference Number", children: Reference },
      { key: "DATE", label: "Credit Date", children: CreditDate },
      { key: "AMOUNT", label: "Credit Amount", children: CreditAmount },
      { key: "TYPE", label: "Credit Type", children: CreditType },
      {
        key: "INVOICE",
        label: "Associated Invoice",
        children: AssociatedInvoice,
      },
      { key: "APPLIED", label: "Applied Amount", children: AppliedAmount },
      {
        key: "UNAPPLIED",
        label: "UnApplied Amount",
        children: UnAppliedAmount,
      },
    ];
  }
  const items = getItems();
  return <DetailsViewLayout items={items} />;
}

export { CreditDetailsComp };
